import { reactive, ref } from 'vue'
import { confirmSignIn, fetchAuthSession, fetchUserAttributes, getCurrentUser, resendSignUpCode, signIn, signUp, signOut, updateUserAttributes } from 'aws-amplify/auth'

type UserAttributes = {
  $id: string | undefined
  email: string | undefined
  isNewUser: boolean
  mailing: {
    communication : boolean,
    security : boolean
  } | object
  name: string | undefined
}

const current = ref<{ accessToken?: string; idToken?: string } | null>(null);
const loading = ref(false);
const error = ref<string | undefined>(undefined);
const jwt = ref<string | undefined>(undefined);
const userAttributes = reactive<UserAttributes>({
  $id: '',
  email: '',
  isNewUser: false,
  mailing: {
    communication : true,
    security : true
  },
  name: ''
});

export function useAuth() {
  async function getUser() {
    try {
      const response = await getCurrentUser();
      return response;
    } catch {
      return {
				i18nKey: "settings.form.userError"
			}
    }
  }

  async function isAuth() {
    try {
      await getCurrentUser();
      return true;
    } catch(err) { return false } 
  }

  async function setUserAttributes() {
    try {
      if (userAttributes.$id) return;

      const response = await fetchUserAttributes();

      userAttributes.email = response.email;
      userAttributes.name = response.name;
      userAttributes.$id = response.sub;
      userAttributes.isNewUser = JSON.parse((response["custom:IsNewUser"] as string));
      userAttributes.mailing = ['communication', 'security'].reduce((prev, next) => ({
          ...prev,
          [next]: response["custom:mailing"]?.includes(next),
        }), {});

      return userAttributes;
    } catch {
      return {
				i18nKey: "settings.form.updateUserError"
			}
    }
  }

  async function updateAttributesUser(aboutUser: object) {
    try {
      await updateUserAttributes({
        userAttributes: aboutUser
      })

      return {
				i18nKey: "settings.form.success"
			}
    } catch {
      return {
				i18nKey: "settings.form.error"
			}
    }
  }

  async function signUpFlow(email: string) {
		try {
			await signUp({
				username: email,
				password: crypto.randomUUID() + "!Ab1",
				options: {
					userAttributes: {
						email,
					},
					autoSignIn: false,
					clientMetadata: { CLIENT_METADATA_PWLESS: '1' }
				}
			});
		} catch (err: Error | unknown) {
			if (err instanceof Error) {
				if (err.name !== 'UsernameExistsException') {
					return err 
				}
			}
		}
	}

	async function signInFlow(email: string) {
		try {
			const response = await signIn({
				username: email.trim(),
				options: {
					authFlowType: 'CUSTOM_WITHOUT_SRP',
				},
			});

			return {
			  // @ts-ignore
				userId: response.nextStep.additionalInfo.USERNAME,
			};
		} catch (err: Error | unknown) {
			if (err instanceof Error) {
				return err;
			}
		}
	}

	async function startLogin(email: string) {
		function response() {
			return {
				i18nKey: 'error/internal-error'
			}
		}

		try {
			await signUpFlow(email);
			await signInFlow(email);
		} catch (err: Error | unknown) {
			if (err instanceof Error) {
				return response()
			}
		}
	}

	async function refreshSession() {
		const s = await fetchAuthSession()

		current.value = {
			accessToken: s.tokens?.accessToken?.toString(),
			idToken: s.tokens?.idToken?.toString()
		}

    jwt.value = s.tokens?.accessToken?.toString();
	}

	async function confirmOtp(code: string) {
		loading.value = true; error.value = undefined

		try {
			await confirmSignIn({ challengeResponse: code })
			await refreshSession()
		} catch (e: any) {
			error.value = e?.message || 'Invalid or expired code'
		} finally { loading.value = false }
	}

	async function resendOtp(email: string | null) {
		if (!email) return

		try {
      await resendSignUpCode({ username: email });

			return {
				code: 200,
				i18nKey: 'login.emailCode.success',
			}
		} catch {
			return {
				code: 400,
				i18nKey: 'login.emailCode.error',
			}
		}
	}

	async function logout() {
	  try {
      await signOut();
      current.value = null;
      jwt.value = undefined;
		} catch {}
	}

  return { confirmOtp, current, error, setUserAttributes, getUser, isAuth, jwt, loading, logout, resendOtp, refreshSession, startLogin, userAttributes, updateAttributesUser }
}
